import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";

type OnChangeFunc<T> = (newVal: T) => void;
export const useDebouncedState = <T>({
  delay = 1000,
  isEqual = Object.is,
  ...props
}: {
  value: T;
  onChange: OnChangeFunc<T>;
  delay?: number;
  isEqual?: (currentValue: T, nextValue: T) => boolean;
}) => {
  const [internalValue, setInternalValue] = useState(props.value);
  const onChangeRef = useRef<OnChangeFunc<T> | undefined>();
  useLayoutEffect(() => {
    onChangeRef.current = props.onChange;
  }, [props.onChange]);

  const debouncedOnChange = useMemo(() => debounce((newVal: T) => onChangeRef.current?.(newVal), delay), [delay]);
  useLayoutEffect(
    () =>
      setInternalValue((currentValue) => {
        if (isEqual(currentValue, props.value)) {
          return currentValue;
        }

        debouncedOnChange.cancel();
        return props.value;
      }),
    [debouncedOnChange, isEqual, props.value],
  );

  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);
  const onChange = useCallback(
    (newVal: T) => {
      setInternalValue(newVal);
      debouncedOnChange(newVal);
    },
    [debouncedOnChange],
  );

  return [internalValue, onChange, debouncedOnChange.flush] as const;
};

export function useDebouncedValue<T>(value: T, setValue: (val: T) => void, delay: number) {
  const [innerValue, setValueCb] = useDebouncedState({
    value,
    onChange: setValue,
    delay,
  });

  return [innerValue, setValueCb] as [T, (val: T) => void];
}

interface UseDelayedUpdateInputProps<T> {
  value: T;
  onChange: (value: T) => void;
}

export function useDelayedUpdateInput<T>({ value, onChange }: UseDelayedUpdateInputProps<T>) {
  const [internalValue, onChangeCb] = useDebouncedState({
    value,
    onChange,
    delay: 500,
  });

  return {
    value: internalValue,
    onChange: onChangeCb,
  };
}
